import { 
  CategoriaDataReferencia, 
  CategoriaEmpregadoEEmpregador, 
  Categorias, 
  CategoriaValor, 
  Separadores, 
  TipoDaVerba, 
  UnidadeDaVerba,
  TipoDelimitacao 
} from './dominio/e02.enum';
import { CampoLayout, Modificadores, ConfiguracaoDelimitacao } from './repositorio/e02.layout.interface';
import { dataFormatada, titulosFormatados, titulosSubCampoEmpregadoEEmpregador, configuracaoCategoriaValor } from './constantes';
import i18n from '@/typescript/servicos/i18n/i18n.servico';

/**
 * Aplica a delimitação (recorte) ao valor conforme configuração
 * Replica a lógica do backend: delimitar_campo
 */
export function aplicarDelimitacao(valor: string, delimitacao?: ConfiguracaoDelimitacao | null): string {
  if (!delimitacao || !valor) return valor;

  const { tipo, quantidade_caracteres } = delimitacao;

  if (quantidade_caracteres <= 0 || valor.length <= quantidade_caracteres) {
    return valor;
  }

  if (tipo === TipoDelimitacao.Inicio) {
    return valor.slice(0, quantidade_caracteres);
  } else {
    return valor.slice(-quantidade_caracteres);
  }
}

/**
 * Monta a prévia do campo "nome do evento". Como o valor real depende do
 * empregado/verba no momento da geração (visual personalizado da regra,
 * rubricas etc.), usamos um nome de exemplo apenas para ilustrar a formatação.
 */
export function formatarPreviaNomeDoEvento(modificadores: Modificadores): string {
  const exemplo = i18n.t('layoutE02.layout.exemploNomeDoEvento') as string;
  const qtdCaractere = modificadores.quantidade_de_caracteres ?? 0;
  const caracterePreenchimento = modificadores.caractere_de_preenchimento ?? '0';
  const alinhamento = modificadores.alinhamento ?? '>';

  let previa = exemplo;
  // largura só preenche (não trunca), espelhando a formatação de texto do backend
  if (qtdCaractere > exemplo.length) {
    previa = alinhamento === '>'
      ? exemplo.padStart(qtdCaractere, caracterePreenchimento)
      : exemplo.padEnd(qtdCaractere, caracterePreenchimento);
  }

  return aplicarDelimitacao(previa, modificadores.delimitacao);
}
function calcularPreviaValor(
  modificadores: Modificadores, 
  formatoValor: CategoriaValor
): string {
  const qtdCaractere = modificadores.quantidade_de_caracteres ?? 0;
  const caracterePreenchimento = modificadores.caractere_de_preenchimento ?? '0';

  if (qtdCaractere <= 0) return '';

  if (formatoValor === CategoriaValor.Decimal) {
    const qtdCasasDecimais = modificadores.quantidade_de_casas_decimais ?? 2;
    const decimalDivisor = modificadores.divisor_de_casas_decimais ?? '.';

    if (qtdCasasDecimais <= 0 || (qtdCaractere < (decimalDivisor.length + 1))) {
      return caracterePreenchimento.repeat(qtdCaractere);
    }

    const qtdCasaDecimal = Math.min(qtdCasasDecimais, qtdCaractere);
    let previa = caracterePreenchimento.repeat(qtdCasaDecimal);

    if (qtdCaractere > previa.length && qtdCaractere >= (previa.length + decimalDivisor.length)) {
      previa = decimalDivisor + previa;
    }

    if (qtdCaractere > previa.length) {
      previa = caracterePreenchimento.repeat(qtdCaractere - previa.length) + previa;
    } else if (previa.length > qtdCaractere) {
      previa = previa.substring(previa.length - qtdCaractere);
    }
    return previa;
  }

  if (formatoValor === CategoriaValor.HorasEMinutos) {
    const separadorPadrao = modificadores.divisor_de_casas_decimais ?? ':'; 
    const minimoCaracterePadrao = 2;
    const minimoCaractere = Math.min(minimoCaracterePadrao, qtdCaractere);
    let previa = caracterePreenchimento.repeat(minimoCaractere);

    if (qtdCaractere > previa.length && qtdCaractere >= (previa.length + separadorPadrao.length)) {
      previa = separadorPadrao + previa;
    }

    if (qtdCaractere > previa.length) {
      previa = caracterePreenchimento.repeat(qtdCaractere - previa.length) + previa;
    } else if (previa.length > qtdCaractere) {
      previa = previa.substring(previa.length - qtdCaractere);
    }
    return previa;
  }
  
 
  return caracterePreenchimento.repeat(qtdCaractere);
}

export function formatarPrevia(campo: CampoLayout): string {
  if (!campo || typeof campo !== 'object') return '';

  const categoria = campo.campo;
  const modificadores = campo.modificadores || {};

  if (categoria === Categorias.Fixo) {
    const texto = modificadores.texto ?? '';
    return aplicarDelimitacao(texto, modificadores.delimitacao);
  }

  if (categoria === Categorias.DataDeReferencia) {
    const formatoDaData = modificadores.formato_da_data as CategoriaDataReferencia;
    return dataFormatada[formatoDaData] ?? '';
  }

  if (categoria === Categorias.NomeDoEvento) {
    return formatarPreviaNomeDoEvento(modificadores);
  }

  if (categoria === Categorias.CampoCustomizavelPorEmpregado) {
    return formatarPreviaCampoCustomizavel(modificadores);
  }

  const qtdCaractere = modificadores.quantidade_de_caracteres ?? 0;
  const caracterePreenchimento = modificadores.caractere_de_preenchimento ?? '0';

  if (qtdCaractere <= 0) return '\u00A0';

  if (categoria === Categorias.Valor) {
    const formatoValor = modificadores.formato_do_valor as CategoriaValor;


    const previaPadrao = formatoValor
      ? calcularPreviaValor(modificadores, formatoValor)
      : caracterePreenchimento.repeat(qtdCaractere); 
      
   
    return aplicarDelimitacao(previaPadrao, modificadores.delimitacao);
  }

  
  const resultado = caracterePreenchimento.repeat(qtdCaractere);
  return aplicarDelimitacao(resultado, modificadores.delimitacao);
}

export function formatarPreviaIndividual(campo: CampoLayout): string {
  if (!campo || typeof campo !== 'object') return '';

  const categoria = campo.campo;
  const modificadores = campo.modificadores || {};

  if (categoria === Categorias.Fixo) {
    return modificadores.texto ?? '';
  }

  if (categoria === Categorias.DataDeReferencia) {
    const formatoDaData = modificadores.formato_da_data as CategoriaDataReferencia;
    return dataFormatada[formatoDaData] ?? '';
  }

  if (categoria === Categorias.Sequencial) {
    const qtdCaractere = modificadores.quantidade_de_caracteres ?? 1;
    const caracterePreenchimento = modificadores.caractere_de_preenchimento ?? '0';
    const alinhamento = modificadores.alinhamento ?? '>';

    if (qtdCaractere <= 0) return '\u00A0';

    const numeroExemplo = '1';
    let previaFormatada = alinhamento === '>'
      ? numeroExemplo.padStart(qtdCaractere, caracterePreenchimento)
      : numeroExemplo.padEnd(qtdCaractere, caracterePreenchimento);

    if (previaFormatada.length > qtdCaractere) {
      previaFormatada = previaFormatada.substring(previaFormatada.length - qtdCaractere);
    }

    return aplicarDelimitacao(previaFormatada, modificadores.delimitacao);
  }

  if (categoria === Categorias.TipoDaVerba) {
    const textoDesconto = modificadores.texto_desconto ?? '-1';
    const textoBonus = modificadores.texto_bonus ?? '1';
    return `${textoDesconto}/${textoBonus}`;
  }

  if (categoria === Categorias.UnidadeDaVerba) {
    const textoDias = modificadores.texto_dias ?? 'D';
    const textoHoras = modificadores.texto_horas ?? 'H';
    return `${textoDias}/${textoHoras}`;
  }

  if (categoria === Categorias.NomeDoEvento) {
    return formatarPreviaNomeDoEvento(modificadores);
  }

  if (categoria === Categorias.CampoCustomizavelPorEmpregado) {
    return formatarPreviaCampoCustomizavel(modificadores);
  }

  const qtdCaractere = modificadores.quantidade_de_caracteres ?? 0;
  const caracterePreenchimento = modificadores.caractere_de_preenchimento ?? '0';

  if (qtdCaractere <= 0) return '\u00A0';

  if (categoria === Categorias.Valor) {
    const formatoValor = modificadores.formato_do_valor as CategoriaValor;
    const formatoEspecial = modificadores.formato_especifico_para_verbas_em_dias as CategoriaValor | null | undefined;
    
    
    let previaPadrao = '';
    if (formatoValor) {
      previaPadrao = calcularPreviaValor(modificadores, formatoValor);
    } else {
      
      const qtdCaractere = modificadores.quantidade_de_caracteres ?? 0;
      const caracterePreenchimento = modificadores.caractere_de_preenchimento ?? '0';
      previaPadrao = caracterePreenchimento.repeat(qtdCaractere);
    }

  
    let previaEspecial = '';
    if (formatoEspecial && formatoEspecial !== null) {
     
      const modificadoresTemporarios = {
        ...modificadores,
        formato_do_valor: formatoEspecial
      };
      
      previaEspecial = calcularPreviaValor(modificadoresTemporarios, formatoEspecial);
      
      if (previaEspecial) {
       
        return `${previaPadrao}/${previaEspecial}`;
      }
    }

  
    return previaPadrao;
  }
  const resultado = caracterePreenchimento.repeat(qtdCaractere);
  return aplicarDelimitacao(resultado, modificadores.delimitacao);
}

function formatarPreviaCampoCustomizavel(modificadores: Modificadores): string {
  const valorPadrao = modificadores.valor_padrao ?? '0';
  const quantidadeDeCaracteres = modificadores.quantidade_de_caracteres ?? 0;
  const caractereDePreenchimento = modificadores.caractere_de_preenchimento ?? '0';
  const alinhamento = modificadores.alinhamento ?? '>';

  if (quantidadeDeCaracteres <= 0) return valorPadrao;

  return alinhamento === '>'
    ? valorPadrao.padStart(quantidadeDeCaracteres, caractereDePreenchimento)
    : valorPadrao.padEnd(quantidadeDeCaracteres, caractereDePreenchimento);
}

export function formatarSequencial(numero: number, modificadores: Modificadores): string {
  const qtdCaracteres = modificadores.quantidade_de_caracteres || 1;
  const preenchedor = modificadores.caractere_de_preenchimento || '0';
  const alinhamento = modificadores.alinhamento || '>';

  let numeroFormatado = String(numero);

  if (alinhamento === '>') {
    numeroFormatado = numeroFormatado.padStart(qtdCaracteres, preenchedor);
  } else {
    numeroFormatado = numeroFormatado.padEnd(qtdCaracteres, preenchedor);
  }

  if (numeroFormatado.length > qtdCaracteres) {
    if (alinhamento === '>') {
      numeroFormatado = numeroFormatado.slice(-qtdCaracteres);
    } else {
      numeroFormatado = numeroFormatado.slice(0, qtdCaracteres);
    }
  }

  return numeroFormatado;
}

export function formatarTipoDaVerba(tipo: TipoDaVerba, modificadores: Modificadores): string {
  const textoDesconto = modificadores.texto_desconto || '-1';
  const textoBonus = modificadores.texto_bonus || '1';
  return tipo === TipoDaVerba.Desconto ? textoDesconto : textoBonus;
}

export function formatarUnidadeDaVerba(unidade: UnidadeDaVerba, modificadores: Modificadores): string {
  const textoDias = modificadores.texto_dias || 'D';
  const textoHoras = modificadores.texto_horas || 'H';
  return unidade === UnidadeDaVerba.Dias ? textoDias : textoHoras;
}

export function formatarPreviaComFormatoEspecialDias(campo: CampoLayout, ehVerbaEmDias: boolean): string {
  if (!campo || campo.campo !== Categorias.Valor) return '';

  const modificadores = campo.modificadores || {};
  const formatoEspecial = modificadores.formato_especifico_para_verbas_em_dias;
  
  const formatoAUsar = ehVerbaEmDias && formatoEspecial 
    ? formatoEspecial 
    : modificadores.formato_do_valor;

  
  const campoTemporario: CampoLayout = {
    ...campo,
    modificadores: {
      ...modificadores,
      formato_do_valor: formatoAUsar
    }
  };

  return formatarPrevia(campoTemporario);
}

export function obterNomeFormatoValor(formato: CategoriaValor | null): string {
  if (formato === null) {
    return i18n.t('layoutE02.layout.labels.formatoEspecificoVerbasEmDias.nenhum') as string;
  }
  
  const itemEncontrado = configuracaoCategoriaValor.loaded.find(item => item.id === formato);
  return itemEncontrado ? (itemEncontrado.name as string) : String(formato);
}
export function obterTooltipFormatoEspecialDias(campo: CampoLayout): string {
  if (!campo || campo.campo !== Categorias.Valor) return '';
  
  const modificadores = campo?.modificadores;
  if (!modificadores?.formato_especifico_para_verbas_em_dias && 
      modificadores?.formato_especifico_para_verbas_em_dias !== null) {
    return '';
  }
  
  const formatoEspecial = modificadores.formato_especifico_para_verbas_em_dias;
  const formatoPadrao = modificadores.formato_do_valor;
  
  const nomeEspecial = obterNomeFormatoValor(formatoEspecial as CategoriaValor | null);
  const nomePadrao = obterNomeFormatoValor(formatoPadrao as CategoriaValor);
  
  return i18n.t('layoutE02.layout.formatoEspecialDias.tooltip', {
    formatoEspecial: nomeEspecial,
    formatoPadrao: nomePadrao
  }) as string;
}

export function separarCampoESubcampo(campoCompleto: string): { campoPrincipal: string; subCampo: string } {
  const [campoPrincipal, ...subCampos] = campoCompleto.split('.');
  const subCampo = subCampos.length > 0 ? subCampos.join('.') : '';
  return { campoPrincipal, subCampo };
}

export function formatarCampoPrincipal(campo: string, subCampo?: string): string {
  if (!subCampo || subCampo.trim() === '') return campo;
  return `${campo}.${subCampo}`;
}

export function formatarCampos(campos: CampoLayout[]) {
  return campos.map((campoOriginal) => {
    const campo = { ...campoOriginal };
    // é necessário alterar o campo principal para 'empregado' quando for 'empregador'
    // para não gerar erro no pós processamento do layout
     // o formato que o gerador de layout utiliza é 'empregado.empregador' para empregador

    if (campo.campo === Categorias.Empregador) {
      campo.campo = Categorias.Empregado;
    }

    const campoPrincipalFormatado = formatarCampoPrincipal(campo.campo, campo.subCampo);

    const campoFormatado = {
      ...campo,
      campo: campoPrincipalFormatado,
    };

    delete campoFormatado.subCampo;
    delete campoFormatado.id;

    return campoFormatado;
  });
}

export function formatarSeparadorParaEnvio(separador: string, qtdEspacos?: number | null): string {
  if (separador === Separadores.Espaco) {
    const total = Number(qtdEspacos) || 1;
    return ' '.repeat(total);
  }
  return separador;
}

export function formatarSeparadorParaLeitura(separador: string | null): { separador: string | null; qtdEspacos: number | null } {
  if (!separador) return { separador: null, qtdEspacos: null };

  const apenasEspacos = /^\s+$/.test(separador);
  // atribui o id 'espaco' para seletor
  // e a quantidade de espaços para o input
  if (apenasEspacos) {
    return { separador: Separadores.Espaco, qtdEspacos: separador.length };
  }
  return { separador, qtdEspacos: null };
}

export function formatarTituloDoCampo(configuracao: CampoLayout): string {
  if (!configuracao || !configuracao.campo) return '';

  const tituloFormatado: string[] = [];

  const tituloCampo = titulosFormatados[configuracao.campo as Categorias];
  if (tituloCampo) tituloFormatado.push(tituloCampo as string);

  const tituloSubCampo = titulosSubCampoEmpregadoEEmpregador[configuracao.subCampo as CategoriaEmpregadoEEmpregador];
  if (tituloSubCampo) tituloFormatado.push(tituloSubCampo as string);

  const modificadores = configuracao.modificadores || {};
  Object.entries(modificadores).forEach(([chave, valor]) => {
    
    if (chave === 'formato_especifico_para_verbas_em_dias') {
      if (valor === undefined) return;
    } else if (valor === null || valor === undefined || valor === '') {
      return;
    }

    
    if (chave === 'delimitacao' && valor) {
      const del = valor as ConfiguracaoDelimitacao;
      const tipoLabel = del.tipo === TipoDelimitacao.Inicio ? 'início' : 'fim';
      tituloFormatado.push(`Delimitação: ${del.quantidade_caracteres} caracteres do ${tipoLabel}`);
      return;
    }

    
    if (chave === 'formato_especifico_para_verbas_em_dias') {
      const valorCategoria = valor as CategoriaValor | null;
      const nomeOpcao = obterNomeFormatoValor(valorCategoria);
      
      tituloFormatado.push(
        i18n.t('layoutE02.layout.modificadoresFormatados.formato_especifico_para_verbas_em_dias', { 
           valor: nomeOpcao 
        }) as string
      );
      return;
    }

    
    if (chave === 'texto_desconto' || chave === 'texto_bonus') {
      const labelTipo = chave === 'texto_desconto'
        ? i18n.t('layoutE02.layout.labels.tipoDaVerba.desconto') as string
        : i18n.t('layoutE02.layout.labels.tipoDaVerba.bonus') as string;
      tituloFormatado.push(`${labelTipo}: ${valor}`);
      return;
    }
    if (chave === 'texto_dias' || chave === 'texto_horas') {
      const labelUnidade = chave === 'texto_dias'
        ? i18n.t('layoutE02.layout.labels.unidadeDaVerba.dias') as string
        : i18n.t('layoutE02.layout.labels.unidadeDaVerba.horas') as string;
      tituloFormatado.push(`${labelUnidade}: ${valor}`);
      return;
    }
    let valorFormatado = valor;

    if (chave === 'formato_da_data' && dataFormatada[valor as CategoriaDataReferencia]) {
      valorFormatado = dataFormatada[valor as CategoriaDataReferencia];
    }

    tituloFormatado.push(i18n.t(`layoutE02.layout.modificadoresFormatados.${chave}`, { valor: valorFormatado }) as string);
  });

  return tituloFormatado.join(' - ');
}
